import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, getCurrentInstance, defineAsyncComponent, nextTick, watch } from 'vue'

import { add } from '@white-label-helper/date-utilities';
import { useDropdownSync, useScreenSize } from '@white-label-helper/shared-composables';

import DateRangePickerInputs from './date-range-picker-inputs/date-range-picker-inputs.vue';
import { CavuCalendar } from 'feature-shared-organisms';
import { Button, Backdrop } from 'ui-shared-components';


export default /*#__PURE__*/_defineComponent({
  __name: 'date-range-picker',
  props: {
    inlineDatePicker: { type: Boolean, default: false },
    startDateLabel: { default: '' },
    endDateLabel: { default: '' },
    presetStartDate: { default: null },
    presetEndDate: { default: null },
    errors: { default: () => ({
      startDate: '',
      endDate: '',
    }) }
  },
  emits: ['open', 'close', 'input', 'input-start-date', 'input-end-date'],
  setup(__props: any, { emit }) {

const props = __props as { inlineDatePicker: boolean, startDateLabel: string, endDateLabel: string, presetStartDate: Date | null, presetEndDate: Date | null, errors: {
      startDate: string;
      endDate: string;
    } };

const IconButton = defineAsyncComponent(() => import('@white-label-icon/icon-button'));
const IconArrow = defineAsyncComponent(() => import('@white-label-icon/icon-arrow'));



// Current value of the dates
const form = ref({
  startDate: null as Date | null,
  endDate: null as Date | null,
})

// To store a copy of the dates for restoring later if needed
const previousDates = ref({
  startDate: form.value.startDate,
  endDate: form.value.endDate,
});

const datePickerOpened = ref(false);
const proxy = getCurrentInstance()?.proxy as Vue;

const { isMobileResolution, isTabletResolution } = useScreenSize();
const isMobileOrTabletResolution = computed(() => {
  return isMobileResolution.value || isTabletResolution.value;
})

// Keeps opened state in sync with other dropdowns and modals
useDropdownSync(datePickerOpened);

const showInputs = computed(() => {
  if (props.inlineDatePicker) return !datePickerOpened.value;
  return true;
});

watch(
  [() => props.presetStartDate, () => props.presetEndDate],
  ([newStart, newEnd]) => {
    if (newStart && !datePickerOpened.value) {
      form.value.startDate = new Date(newStart);
      emit('input-start-date', form.value.startDate);
    }
    if (newEnd && !datePickerOpened.value) {
      form.value.endDate = new Date(newEnd);
      emit('input-end-date', form.value.endDate);
    }
  },
  { immediate: true },
)

const computedDates = computed<Date[]>(() => {
  return [form.value.startDate, form.value.endDate].filter(date => date !== null);
})

const openDatePicker = async (input: 'start' | 'end') => {
  // Copy current date values to restore later if needed
  previousDates.value.startDate = form.value.startDate;
  previousDates.value.endDate = form.value.endDate;

  // If opening from the start date input, clear both dates
  if (props.inlineDatePicker) {
    if (input === 'start') {
      form.value.startDate = null;
      form.value.endDate = null;
    } else {
      // If opening from the end date input, clear only the end date
      form.value.endDate = null;
    }
  }

  datePickerOpened.value = true;
  emit('open');
};

const closeDatePicker = async () => {
  // If datepicker was closed without saving, restore the original dates
  if (props.inlineDatePicker) {
    if (!form.value.startDate && previousDates.value.startDate) {
      form.value.startDate = previousDates.value.startDate;
    }
    if (!form.value.endDate && previousDates.value.endDate) {
      form.value.endDate = previousDates.value.endDate;
    }
    //emits restored dates back to form
    emit('input', { startDate: form.value.startDate, endDate: form.value.endDate });
  }

  if (!props.inlineDatePicker) {
    const startChanged = form.value.startDate?.getTime() !== previousDates.value.startDate?.getTime();
    const endChanged = form.value.endDate?.getTime() !== previousDates.value.endDate?.getTime();
    if (startChanged || endChanged) {
      emit('input', { startDate: form.value.startDate, endDate: form.value.endDate });
    }
  }

  datePickerOpened.value = false;
  await nextTick();
  emit('close');

  // If no start date, focus on the start date button
  if (!form.value.startDate) {
    const startDateButton = proxy.$el.querySelector('#date-picker__start-date') as HTMLButtonElement;
    startDateButton?.focus();
  } else {
    // Otherwise focus on the end date button
    const endDateButton = proxy.$el.querySelector('#date-picker__end-date') as HTMLButtonElement;
    endDateButton?.focus();
  }
};

const onClickBack = (): void => {
  if (!form.value.startDate) {
    closeDatePicker();
  } else if (!form.value.endDate) {
    form.value.startDate = null;
  } else {
    form.value.endDate = null;
  }
};

const onChangeDate = (dates: Date[]) => {
  const [date1, date2] = dates;

  form.value.startDate = date1 ?? null;
  form.value.endDate = date2 ?? null;

  if (form.value.startDate && form.value.endDate &&
    new Date(form.value.startDate) > new Date(form.value.endDate)) {
    form.value.endDate = null;
  } else if (form.value.startDate && form.value.endDate)  {
    // If both dates are selected, emit the input event with the new dates
    emit('input', { startDate: form.value.startDate, endDate: form.value.endDate });
  }

  if (!props.inlineDatePicker && form.value.startDate && form.value.endDate) {
    // If both dates are selected, close the date picker
    closeDatePicker();
  }
};

const headerLabel = computed(() => {
  if (!form.value.startDate) return proxy.$t('UI.dateRangeInput.placeholder.startDate') as string;
  if (!form.value.endDate) return proxy.$t('UI.dateRangeInput.placeholder.endDate') as string;
  return proxy.$t('UI.dateRangeInput.yourTripDates') as string;
});

const displayFieldBorder = computed(() => {
  if (!datePickerOpened.value) return 'none';
  if (!form.value.startDate || (form.value.startDate && form.value.endDate)) return 'start';
  if (form.value.startDate && !form.value.endDate) return 'end';
  return 'none';
})

const saveDatesButton = ref<Vue | null>(null);

const handleCalendarEscPressed = () => {
  if (form.value.startDate && form.value.endDate) {
    const el = saveDatesButton.value?.$el as HTMLElement | undefined;
    const button = (el?.tagName === 'BUTTON' ? el : el?.querySelector('button')) as HTMLButtonElement | null;
    button?.focus();
  } else {
    closeDatePicker();
  }
};

const handleCalendarTabPressed = (event: KeyboardEvent) => {
  if (form.value.startDate && form.value.endDate) {
    event.preventDefault();
    event.stopPropagation();
    const el = saveDatesButton.value?.$el as HTMLElement;
    const button = (el?.tagName === 'BUTTON' ? el : el?.querySelector('button')) as HTMLButtonElement;
    button?.focus();
  }
  // no save button → don't prevent, Tab enters the calendar naturally
}



return { __sfc: true,IconButton, IconArrow, props, form, previousDates, datePickerOpened, proxy, isMobileResolution, isTabletResolution, isMobileOrTabletResolution, showInputs, computedDates, openDatePicker, closeDatePicker, onClickBack, onChangeDate, headerLabel, displayFieldBorder, saveDatesButton, handleCalendarEscPressed, handleCalendarTabPressed, emit, add, DateRangePickerInputs, CavuCalendar, Button, Backdrop }
}

})